/**
 * General session storage utilities.
 * Provides reusable functions for managing sessionStorage across different features.
 *
 * SECURITY NOTES:
 * - sessionStorage is same-origin only (no CSRF risk)
 * - All functions have SSR guards for server-side rendering safety
 * - Input validation on read to handle corrupted data
 * - Size limits to prevent QuotaExceededError
 * - No sensitive data should be stored (PII, tokens, passwords)
 * - Data is not encrypted (use only for non-sensitive public data)
 *
 * USAGE:
 * - For product selections, user preferences, UI state
 * - NOT for authentication tokens, payment info, or PII
 */

// Maximum session storage size (50KB safety limit to prevent quota errors)
const MAX_SESSION_SIZE = 50000;

/**
 * Build sessionStorage key with site identifier and custom key name.
 * @param {string|null} [siteId] - Site identifier
 * @param {string} keySession - Custom session key name (e.g., 'customize-latest', 'cart', 'filters')
 * @returns {string} Formatted session storage key
 */
export function buildSessionKey(siteId, keySession) {
  return `kl-${keySession}-${siteId ?? 'default'}`;
}

export function buildCustomizeByMasterSessionKey(siteId) {
  return `kl-${siteId ?? 'default'}-customize-by-master`;
}

/**
 * Read session data from sessionStorage.
 * @param {string} sessionKey - Session storage key
 * @returns {Object|null} Stored data object or null
 */
export function readSession(sessionKey) {
  // SSR guard - defense in depth
  if (typeof window === 'undefined') return null;

  try {
    const raw = sessionStorage.getItem(sessionKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    // Validate data structure
    if (typeof parsed !== 'object' || parsed === null) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Session] Invalid data type, clearing session');
      }

      sessionStorage.removeItem(sessionKey);

      return null;
    }

    return parsed;
  } catch (error) {
    // Invalid JSON, quota error, or other storage error
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Session] Read error, clearing session:', error);
    }

    try {
      sessionStorage.removeItem(sessionKey);
    } catch { /* ignore cleanup error */ }

    return null;
  }
}

/**
 * Write data to sessionStorage.
 * @param {string} sessionKey - Session storage key
 * @param {*} data - Data to store (will be JSON stringified)
 */
export function writeSession(sessionKey, data) {
  if (typeof window === 'undefined') return;

  try {
    if (data == null) {
      sessionStorage.removeItem(sessionKey);
    } else {
      const serialized = JSON.stringify(data);

      // Check size to prevent QuotaExceededError
      if (serialized.length > MAX_SESSION_SIZE) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Session] Data too large, skipping persistence:', serialized.length, 'bytes');
        }

        return;
      }

      sessionStorage.setItem(sessionKey, serialized);
    }
  } catch (error) {
    // QuotaExceededError or other storage error
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Session] Write error:', error);
    }
    // Silent failure in production - session is non-critical
  }
}

export function readCustomizeForProduct(sessionKey, masterProductId) {
  if (!masterProductId) return null;

  const entry = readSession(sessionKey)?.[masterProductId];

  return entry?.mode === 'customize' ? entry : null;
}

/**
 * Persist customize selection to sessionStorage.
 * Only persists 'customize' mode; 'set' mode is not persisted.
 * Images/thumbnails are stripped (large, re-fetched on mount).
 * sourceSetId is excluded (not needed in session).
 *
 * @param {string} sessionKey - Session storage key
 * @param {string} masterProductId - Master PDP product id (map key)
 * @param {Object} selection - Selection object to persist
 */
export function persistCustomizeSelection(sessionKey, masterProductId, selection) {
  if (typeof window === 'undefined' || !masterProductId) return;

  try {
    if (selection.mode !== 'customize') {
      clearCustomizeForProduct(sessionKey, masterProductId);

      return;
    }

    const stripped = {
      mode: selection.mode,
      productId: selection.productId,
      deviatedFromSet: selection.deviatedFromSet,
      selections: Object.fromEntries(
        Object.entries(selection.selections ?? {}).map(([stepId, value]) => {
          if (stepId === 'cover') {
            // eslint-disable-next-line no-unused-vars
            const {images, thumbnail, ...rest} = value ?? {};

            return [stepId, rest];
          }

          if (Array.isArray(value)) {
            return [
              stepId,
              value.map((slot) => {
                if (!slot) return slot;

                // eslint-disable-next-line no-unused-vars
                const {images, thumbnail, imageUrl, ...rest} = slot;

                return rest;
              })
            ];
          }

          return [stepId, value];
        })
      ),
    };

    writeSession(sessionKey, {
      ...(readSession(sessionKey) ?? {}),
      [masterProductId]: stripped,
    });
  } catch (error) {
    // QuotaExceededError or other storage error
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Session] Customize persist error:', error);
    }
    // Silent failure in production - session is non-critical
  }
}

export function clearCustomizeForProduct(sessionKey, masterProductId) {
  if (typeof window === 'undefined' || !masterProductId) return;

  try {
    const map = readSession(sessionKey);

    if (!map?.[masterProductId]) return;

    const {[masterProductId]: _, ...rest} = map;

    if (Object.keys(rest).length === 0) {
      clearSession(sessionKey);
    } else {
      writeSession(sessionKey, rest);
    }
  } catch { /* ignore */ }
}

/**
 * Clear session data for a specific session key.
 * @param {string} sessionKey - Session storage key to clear
 */
export function clearSession(sessionKey) {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(sessionKey);
  } catch { /* ignore */ }
}

/**
 * Clear customize session data for a site (call on logout).
 * @param {string|null} [siteId] - Site identifier
 */
export function clearCustomizeSessionData(siteId) {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(buildCustomizeByMasterSessionKey(siteId));
  } catch { /* ignore */ }
}
